import React, { useState, useCallback, useMemo } from 'react';
import swal from 'sweetalert';
import styles from './PaymentInvoices.module.css';
import InvoiceTableRow from './InvoiceTableRow';
import BulkEmailStatusModal from './BulkEmailStatusModal';

const COL_HEADERS = [
  '',
  'Invoice',
  'Company',
  'Place',
  'Period',
  'Orders',
  'Sale Value',
  'KRV Fee',
  'Net Payout',
  'Status',
  'Actions',
];

const InvoiceTable = ({
  status,
  invoices = [],
  totalSize = 0,
  page = 0,
  totalPages = 0,
  pageSize = 15,
  onPageChange,
  onRowClick,
  onViewFull,
  onDownloadClick,
  onStatusClick,
  onSendSelected,
  isSendingStatus = false,
  sendStatusError = null,
  onMarkAsPaid,
  isMarkingPaid = false,
  markPaidError = null,
  onRegenerate,
}) => {
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [bulkEmailModal, setBulkEmailModal] = useState({
    open: false,
    eligible: [],
    excluded: 0,
  });
  const [bulkPaidModal, setBulkPaidModal] = useState({
    open: false,
    eligible: [],
    excluded: 0,
  });

  const invoiceStatusMap = useMemo(
    () =>
      Object.fromEntries(
        invoices.map(inv => [inv.companyInvoiceId, inv.status]),
      ),
    [invoices],
  );

  const handleSelect = useCallback(
    (id, checked) => {
      setSelectedInvoices(prev => {
        if (checked) {
          const inv = invoices.find(i => i.companyInvoiceId === id);
          return inv ? [...prev, inv] : prev;
        }
        return prev.filter(i => i.companyInvoiceId !== id);
      });
    },
    [invoices],
  );

  const handleSelectAll = useCallback(
    e => {
      setSelectedInvoices(e.target.checked ? [...invoices] : []);
    },
    [invoices],
  );

  const handleSendEmailClick = useCallback(() => {
    if (selectedInvoices.length === 0) {
      swal({ text: 'Please select at least one invoice to send.', icon: 'error' });
      return;
    }
    const eligible = selectedInvoices.filter(
      inv => invoiceStatusMap[inv.companyInvoiceId] !== 'PAID',
    );
    const excluded = selectedInvoices.length - eligible.length;
    if (eligible.length === 0) {
      swal({ text: 'All selected invoices are already PAID and cannot be updated.', icon: 'warning' });
      return;
    }
    setBulkEmailModal({ open: true, eligible, excluded });
  }, [selectedInvoices, invoiceStatusMap]);

  const handleBulkEmailClose = useCallback(() => {
    setBulkEmailModal({ open: false, eligible: [], excluded: 0 });
  }, []);

  const handleBulkEmailConfirm = useCallback(async () => {
    const ok = await onSendSelected(
      bulkEmailModal.eligible.map(inv => inv.companyInvoiceId),
    );
    if (ok) handleBulkEmailClose();
  }, [onSendSelected, bulkEmailModal.eligible, handleBulkEmailClose]);

  const handleMarkAsPaidClick = useCallback(() => {
    if (selectedInvoices.length === 0) {
      swal({ text: 'Please select at least one invoice.', icon: 'error' });
      return;
    }
    const eligible = selectedInvoices.filter(
      inv => invoiceStatusMap[inv.companyInvoiceId] !== 'PAID',
    );
    const excluded = selectedInvoices.length - eligible.length;
    if (eligible.length === 0) {
      swal({ text: 'All selected invoices are already PAID.', icon: 'warning' });
      return;
    }
    setBulkPaidModal({ open: true, eligible, excluded });
  }, [selectedInvoices, invoiceStatusMap]);

  const handleBulkPaidClose = useCallback(() => {
    setBulkPaidModal({ open: false, eligible: [], excluded: 0 });
  }, []);

  const handleBulkPaidConfirm = useCallback(async () => {
    const ok = await onMarkAsPaid(
      bulkPaidModal.eligible.map(inv => inv.companyInvoiceId),
    );
    if (ok) handleBulkPaidClose();
  }, [onMarkAsPaid, bulkPaidModal.eligible, handleBulkPaidClose]);

  const from = page * pageSize;
  const to = Math.min(from + pageSize, totalSize);
  const isLoading = status === 'loading' || status === 'refreshing';

  const renderPageButtons = () => {
    if (totalPages <= 1) return null;
    const buttons = [];
    buttons.push(
      <button
        key="prev"
        className={`${styles.tblPageBtn} ${
          page === 0 ? styles.tblPageBtnDisabled : ''
        }`}
        onClick={() => onPageChange(page - 1)}
      >
        ‹
      </button>,
    );
    for (let i = 0; i < Math.min(totalPages, 5); i++) {
      buttons.push(
        <button
          key={i}
          className={`${styles.tblPageBtn} ${
            page === i ? styles.tblPageBtnActive : ''
          }`}
          onClick={() => onPageChange(i)}
        >
          {i + 1}
        </button>,
      );
    }
    if (totalPages > 5) {
      buttons.push(
        <button
          key="ellipsis"
          className={`${styles.tblPageBtn} ${styles.tblPageBtnDisabled}`}
        >
          …
        </button>,
      );
      buttons.push(
        <button
          key="last"
          className={styles.tblPageBtn}
          onClick={() => onPageChange(totalPages - 1)}
        >
          {totalPages}
        </button>,
      );
    }
    buttons.push(
      <button
        key="next"
        className={`${styles.tblPageBtn} ${
          page >= totalPages - 1 ? styles.tblPageBtnDisabled : ''
        }`}
        onClick={() => onPageChange(page + 1)}
      >
        ›
      </button>,
    );
    return buttons;
  };

  return (
    <div className={styles.tblWrap}>
      {/* Toolbar */}
      <div className={styles.tblToolbar}>
        <div className={styles.tblToolbarLeft}>
          <input
            type="checkbox"
            className={styles.cb}
            onChange={handleSelectAll}
          />
          <span className={styles.tblSelInfo}>
            <span className={styles.tblSelCount}>
              {selectedInvoices.length}
            </span>{' '}
            selected · {totalSize.toLocaleString()} invoices
          </span>
        </div>
        <div className={styles.tblToolbarRight}>
          <button
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            onClick={handleSendEmailClick}
          >
            <span role="img" aria-label="email">
              📧
            </span>{' '}
            Send Email
          </button>
          <button
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
            onClick={handleMarkAsPaidClick}
          >
            💵 Change Status To Paid
          </button>
          {/*
          <button className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`} onClick={onRegenerate}>🔄 Regenerate</button> */}
        </div>
      </div>

      {/* Header */}
      <div className={`${styles.tblHd} ${styles.tblColGrid}`}>
        {COL_HEADERS.map((h, i) => (
          <span key={i}>{h}</span>
        ))}
      </div>

      {/* Rows */}
      {isLoading && <div className={styles.tblLoading}>Loading invoices…</div>}
      {!isLoading && status === 'error' && (
        <div className={styles.tblError}>
          Failed to load invoices. Please check your connection and try again.
        </div>
      )}
      {!isLoading && status !== 'error' && invoices.length === 0 && (
        <div className={styles.tblEmpty}>No invoices found.</div>
      )}
      {!isLoading &&
        invoices.map(inv => (
          <InvoiceTableRow
            key={inv.companyInvoiceId}
            invoice={inv}
            selected={selectedInvoices.some(
              i => i.companyInvoiceId === inv.companyInvoiceId,
            )}
            onSelect={handleSelect}
            onView={onRowClick}
            onViewFull={onViewFull}
            onDownloadClick={onDownloadClick}
            onStatusClick={onStatusClick}
          />
        ))}

      {/* Pagination */}
      <div className={styles.tblPagination}>
        <span className={styles.tblPageInfo}>
          {totalSize > 0
            ? `Showing ${from + 1}–${to} of ${totalSize.toLocaleString()}`
            : 'No results'}
        </span>
        <div className={styles.tblPageBtns}>{renderPageButtons()}</div>
      </div>

      <BulkEmailStatusModal
        isOpen={bulkEmailModal.open}
        eligibleCount={bulkEmailModal.eligible.length}
        excludedCount={bulkEmailModal.excluded}
        isUpdating={isSendingStatus}
        updateError={sendStatusError}
        onConfirm={handleBulkEmailConfirm}
        onClose={handleBulkEmailClose}
      />
      <BulkEmailStatusModal
        isOpen={bulkPaidModal.open}
        eligibleCount={bulkPaidModal.eligible.length}
        excludedCount={bulkPaidModal.excluded}
        isUpdating={isMarkingPaid}
        updateError={markPaidError}
        actionLabel="Mark as Paid"
        actionBg="#15803d"
        onConfirm={handleBulkPaidConfirm}
        onClose={handleBulkPaidClose}
      />
    </div>
  );
};

export default InvoiceTable;
